import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Textarea } from './ui/textarea';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { useVoiceInput } from '../hooks/useVoiceInput';
import {
  Send, Loader2, Bot, User, Mic, MicOff, Square, Volume2, VolumeX,
  Copy, CheckCircle2, AlertCircle, X, ArrowRight, Zap, Shield,
  ChevronDown, Play, Pause, SkipForward, MessageSquare, Sparkles,
  Archive, Trash2, Languages, FileText, ListTodo, PenLine
} from 'lucide-react';

const API = '/api';

const QUICK_ACTIONS = [
  { label: 'Summarize', prompt: 'Summarize this message and suggest next steps', icon: FileText },
  { label: 'Draft Reply', prompt: 'Help me draft a professional reply to this message', icon: PenLine },
  { label: 'Extract Tasks', prompt: 'Extract all tasks and action items from this conversation', icon: ListTodo },
  { label: 'Translate', prompt: 'Translate this message to English', icon: Languages },
  { label: 'Archive', prompt: 'Archive this message', icon: Archive },
];

const SwitchboardAIPanel = ({ conversationContext, onAction, onNextMessage, onPrevMessage, messages: threadMessages }) => {
  const [chatMessages, setChatMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `sbai_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const [pendingActions, setPendingActions] = useState([]);
  
  // Live voice mode
  const [liveMode, setLiveMode] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(true);
  const [autoListening, setAutoListening] = useState(false);
  const [playingAudio, setPlayingAudio] = useState(false);
  
  // Auto-triage
  const [triageResults, setTriageResults] = useState(null);
  const [triaging, setTriaging] = useState(false);
  const [triageNotes, setTriageNotes] = useState({});
  
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const audioRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      const el = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]') || scrollRef.current;
      el.scrollTop = el.scrollHeight;
    }
  }, [chatMessages]);

  // Build full conversation context for AI
  const buildContext = useCallback(() => {
    if (!conversationContext) return null;
    const ctx = { ...conversationContext };
    // Include full thread messages if available
    if (threadMessages?.length > 0) {
      ctx.messages = threadMessages.map(m => ({
        sender_name: m.sender_name || m.from_name || m.from || 'Unknown',
        body: m.body || m.text || m.snippet || '',
        timestamp: m.date || m.timestamp || '',
        from: m.from_email || m.from || ''
      }));
    }
    return ctx;
  }, [conversationContext, threadMessages]);

  // Send message to AI
  const sendMessage = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    
    setInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: msg, ts: Date.now() }]);
    setLoading(true);

    try {
      const res = await fetch(`${API}/ai/switchboard/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: msg,
          session_id: sessionId,
          conversation_context: buildContext()
        })
      });

      if (res.ok) {
        const data = await res.json();
        const aiMsg = { role: 'assistant', content: data.message || data.response, ts: Date.now(), actions: data.actions || [], voice_summary: data.voice_summary };
        setChatMessages(prev => [...prev, aiMsg]);

        // Handle actions
        if (data.actions?.length > 0) {
          const pending = data.actions.filter(a => a.requires_confirmation);
          const auto = data.actions.filter(a => !a.requires_confirmation);
          
          // Auto-execute non-confirmation actions
          for (const action of auto) {
            if (action.type === 'next_message' && onNextMessage) {
              onNextMessage();
            } else if (action.type === 'push_to_composer' && onAction) {
              onAction(action);
            } else {
              executeAction(action);
            }
          }
          
          if (pending.length > 0) {
            setPendingActions(prev => [...prev, ...pending]);
          }
        }

        // TTS in live mode
        if (liveMode && ttsEnabled && data.voice_summary) {
          await playTTS(data.voice_summary);
        }
      } else {
        setChatMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error processing your request.', ts: Date.now() }]);
      }
    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: `Error: ${err.message}`, ts: Date.now() }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, sessionId, buildContext, liveMode, ttsEnabled, onNextMessage, onAction]);

  // Execute an action
  const executeAction = async (action) => {
    try {
      const res = await fetch(`${API}/ai/switchboard/execute-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action, session_id: sessionId })
      });
      if (res.ok) {
        const result = await res.json();
        if (result.type === 'next_message' && onNextMessage) {
          onNextMessage();
        } else if (onAction) {
          onAction(result);
        }
        toast.success(`Action executed: ${action.label || action.type}`);
        setPendingActions(prev => prev.filter(a => a !== action));
      }
    } catch { toast.error('Action failed'); }
  };

  const rejectAction = (action) => {
    setPendingActions(prev => prev.filter(a => a !== action));
    toast.info('Action dismissed');
  };

  // TTS playback
  const playTTS = async (text) => {
    try {
      setPlayingAudio(true);
      const res = await fetch(`${API}/ai/tts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ text })
      });
      if (res.ok) {
        const data = await res.json();
        const audio = new Audio(`data:audio/mp3;base64,${data.audio}`);
        audioRef.current = audio;
        audio.onended = () => {
          setPlayingAudio(false);
          // In live mode, auto-start listening after TTS finishes
          if (liveMode) {
            setTimeout(() => startRecording(), 300);
          }
        };
        await audio.play();
      }
    } catch { setPlayingAudio(false); }
  };

  const stopAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPlayingAudio(false);
  };

  // Voice input
  const { isRecording, isTranscribing, duration, startRecording, stopRecording, cancelRecording } = useVoiceInput({
    onTranscript: async (text) => {
      if (liveMode) {
        // Check for verbal approval
        const lower = text.toLowerCase();
        if (pendingActions.length > 0) {
          if (lower.includes('yes') || lower.includes('approve') || lower.includes('go ahead') || lower.includes('confirm')) {
            executeAction(pendingActions[0]);
            return;
          }
          if (lower.includes('no') || lower.includes('reject') || lower.includes('skip') || lower.includes('cancel')) {
            rejectAction(pendingActions[0]);
            return;
          }
        }
        // Check for navigation commands
        if (lower.includes('next message') || lower.includes('move on') || lower.includes('skip this')) {
          if (onNextMessage) onNextMessage();
          return;
        }
        // Auto-send
        await sendMessage(text);
      } else {
        setInput(prev => prev ? prev + ' ' + text : text);
        inputRef.current?.focus();
      }
    }
  });

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copied to clipboard'));
  };

  // Push to composer (the message compose box in the conversation pane)
  const pushToComposer = (text) => {
    if (onAction) onAction({ type: 'push_to_composer', data: { text } });
    toast.success('Pushed to message composer');
  };

  // Auto-triage
  const startAutoTriage = async () => {
    if (!threadMessages?.length) { toast.error('No messages to triage'); return; }
    setTriaging(true);
    try {
      const res = await fetch(`${API}/ai/switchboard/auto-triage`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ messages: threadMessages.slice(0, 10) })
      });
      if (res.ok) {
        const data = await res.json();
        setTriageResults(data.results || []);
      } else { toast.error('Triage failed'); }
    } catch { toast.error('Triage error'); }
    finally { setTriaging(false); }
  };

  const approveTriageResult = async (result, approved, userCategory) => {
    const notes = triageNotes[result.id] || '';
    if (notes || !approved) {
      // Save as triage rule
      const ruleText = approved 
        ? `Messages from "${result.from}" about "${result.subject}" should be categorized as ${result.category}. ${notes}`
        : `Messages like "${result.subject}" should be categorized as ${userCategory || result.category}, not ${result.category}. Reason: ${notes}`;
      
      try {
        await fetch(`${API}/ai/switchboard/triage-rule`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({
            rule: ruleText,
            message_id: result.id,
            original_category: result.category,
            user_category: approved ? result.category : userCategory,
            notes
          })
        });
        toast.success(approved ? 'Triage approved & rule saved' : 'Feedback saved as rule');
      } catch { toast.error('Failed to save rule'); }
    }
    setTriageResults(prev => prev.filter(r => r.id !== result.id));
  };

  const categoryColors = {
    urgent_action: 'bg-red-100 text-red-700 border-red-200',
    awaiting: 'bg-amber-100 text-amber-700 border-amber-200',
    fyi: 'bg-blue-100 text-blue-700 border-blue-200',
    delegate: 'bg-purple-100 text-purple-700 border-purple-200',
    archive: 'bg-slate-100 text-slate-600 border-slate-200'
  };

  return (
    <div className="flex flex-col h-full bg-card" data-testid="switchboard-ai-panel">
      {/* Header with live mode toggle */}
      <div className="px-3 py-2.5 border-b flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
            <Bot className="w-4 h-4 text-white" />
          </div>
          <div>
            <span className="text-sm font-semibold">Orka AI</span>
            {liveMode && <Badge className="ml-1.5 text-[9px] bg-red-500 text-white animate-pulse">LIVE</Badge>}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {/* Auto-Triage */}
          <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={startAutoTriage} disabled={triaging} data-testid="auto-triage-btn">
            {triaging ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            Triage
          </Button>
          {/* TTS toggle */}
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setTtsEnabled(!ttsEnabled); if (playingAudio) stopAudio(); }}
            title={ttsEnabled ? 'Mute voice' : 'Enable voice'} data-testid="tts-toggle">
            {ttsEnabled ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
          </Button>
          {/* Live mode toggle */}
          <Button
            variant={liveMode ? "default" : "outline"}
            size="sm"
            className={cn("h-7 text-xs gap-1", liveMode && "bg-red-500 hover:bg-red-600 text-white")}
            onClick={() => { setLiveMode(!liveMode); if (liveMode) { stopAudio(); } }}
            data-testid="live-mode-toggle"
          >
            {liveMode ? <><Pause className="w-3 h-3" />Stop Live</> : <><Play className="w-3 h-3" />Go Live</>}
          </Button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="px-3 py-1.5 border-b flex gap-1 overflow-x-auto flex-shrink-0">
        {QUICK_ACTIONS.map(qa => (
          <Button key={qa.label} variant="ghost" size="sm" className="h-6 text-[10px] flex-shrink-0 gap-1"
            onClick={() => sendMessage(qa.prompt)} disabled={loading} data-testid={`quick-action-${qa.label.toLowerCase().replace(' ', '-')}`}>
            <qa.icon className="w-3 h-3" />{qa.label}
          </Button>
        ))}
      </div>

      {/* Auto-Triage Results */}
      {triageResults && triageResults.length > 0 && (
        <div className="border-b">
          <div className="px-3 py-1.5 bg-amber-50 dark:bg-amber-950/20 flex items-center justify-between">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-700">Auto-Triage Results ({triageResults.length})</span>
            <Button variant="ghost" size="sm" className="h-5 text-[10px]" onClick={() => setTriageResults(null)}><X className="w-3 h-3" /></Button>
          </div>
          <ScrollArea className="max-h-48">
            {triageResults.map((r, i) => (
              <div key={r.id || i} className="px-3 py-2 border-b last:border-b-0 space-y-1.5" data-testid={`triage-result-${i}`}>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={cn("text-[9px]", categoryColors[r.category])}>{r.category}</Badge>
                  <span className="text-xs font-medium truncate flex-1">{r.subject || r.from || 'Message'}</span>
                  <Badge variant="outline" className="text-[9px]">P{r.priority || 3}</Badge>
                </div>
                <p className="text-[10px] text-muted-foreground">{r.reason}</p>
                <p className="text-[10px] text-muted-foreground italic">{r.suggested_action}</p>
                <Textarea
                  value={triageNotes[r.id] || ''}
                  onChange={e => setTriageNotes(prev => ({...prev, [r.id]: e.target.value}))}
                  placeholder="Notes on why you agree/disagree (becomes a rule)..."
                  rows={1}
                  className="text-[10px] h-6 min-h-[24px] resize-none"
                />
                <div className="flex gap-1">
                  <Button size="sm" className="h-5 text-[10px] bg-emerald-600" onClick={() => approveTriageResult(r, true)}>Approve</Button>
                  <Button size="sm" variant="outline" className="h-5 text-[10px]" onClick={() => approveTriageResult(r, false, 'fyi')}>Disagree</Button>
                </div>
              </div>
            ))}
          </ScrollArea>
        </div>
      )}

      {/* Chat Messages */}
      <ScrollArea className="flex-1" ref={scrollRef}>
        <div className="p-3 space-y-3">
          {chatMessages.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Bot className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-xs">I can read the full conversation, help you draft replies, extract tasks, translate, and more. Use quick actions or ask me anything.</p>
              {liveMode && <p className="text-xs mt-2 text-violet-500 font-medium">Live mode is on. I'm listening...</p>}
            </div>
          )}
          {chatMessages.map((msg, i) => (
            <div key={i} className={cn("flex gap-2", msg.role === 'user' ? "justify-end" : "")}>
              {msg.role === 'assistant' && (
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Bot className="w-3 h-3 text-white" />
                </div>
              )}
              <div className={cn(
                "max-w-[85%] rounded-lg px-3 py-2 text-sm",
                msg.role === 'user'
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted"
              )}>
                <p className="whitespace-pre-wrap text-xs">{msg.content}</p>
                {/* Action buttons on AI messages */}
                {msg.role === 'assistant' && (
                  <div className="flex gap-1 mt-1.5 flex-wrap">
                    <Button variant="ghost" size="sm" className="h-5 text-[10px] px-1.5" onClick={() => copyToClipboard(msg.content)}>
                      <Copy className="w-2.5 h-2.5 mr-0.5" />Copy
                    </Button>
                    <Button variant="ghost" size="sm" className="h-5 text-[10px] px-1.5" onClick={() => pushToComposer(msg.content)}>
                      <ArrowRight className="w-2.5 h-2.5 mr-0.5" />To Composer
                    </Button>
                    {msg.voice_summary && ttsEnabled && (
                      <Button variant="ghost" size="sm" className="h-5 text-[10px] px-1.5" onClick={() => playTTS(msg.voice_summary)}>
                        <Volume2 className="w-2.5 h-2.5 mr-0.5" />Listen
                      </Button>
                    )}
                  </div>
                )}
              </div>
              {msg.role === 'user' && (
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <User className="w-3 h-3" />
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div className="flex gap-2">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                <Bot className="w-3 h-3 text-white" />
              </div>
              <div className="bg-muted rounded-lg px-3 py-2">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Pending Actions / Approval Cards */}
      {pendingActions.length > 0 && (
        <div className="border-t px-3 py-2 space-y-1.5 flex-shrink-0 bg-amber-50/50 dark:bg-amber-950/10">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-700">Pending Approval</span>
          {pendingActions.map((action, i) => (
            <div key={i} className="flex items-center gap-2 p-2 rounded-md bg-card border text-xs" data-testid={`pending-action-${i}`}>
              <Shield className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
              <span className="flex-1 truncate">{action.label || action.type}</span>
              <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => rejectAction(action)}>Reject</Button>
              <Button size="sm" className="h-6 text-[10px]" onClick={() => executeAction(action)}>Approve</Button>
            </div>
          ))}
          {liveMode && <p className="text-[9px] text-amber-600 text-center">Say "yes" to approve or "no" to reject</p>}
        </div>
      )}

      {/* Input area */}
      <div className="p-2.5 border-t flex-shrink-0 space-y-1.5">
        {/* Live mode status bar */}
        {liveMode && (
          <div className={cn("flex items-center justify-center gap-2 py-1 rounded-md text-xs",
            isRecording ? "bg-red-100 text-red-700" : playingAudio ? "bg-violet-100 text-violet-700" : "bg-emerald-100 text-emerald-700"
          )}>
            {isRecording ? (
              <><div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />Listening... ({duration}s)</>
            ) : playingAudio ? (
              <><Volume2 className="w-3 h-3 animate-pulse" />Speaking...</>
            ) : isTranscribing ? (
              <><Loader2 className="w-3 h-3 animate-spin" />Processing...</>
            ) : (
              <><Mic className="w-3 h-3" />Ready — speak or type</>
            )}
            {onNextMessage && (
              <Button variant="ghost" size="sm" className="h-5 text-[10px] gap-0.5 ml-2" onClick={onNextMessage}>
                <SkipForward className="w-3 h-3" />Next
              </Button>
            )}
          </div>
        )}
        
        <div className="flex gap-1.5 items-center">
          {/* Voice button */}
          {isRecording ? (
            <div className="flex items-center gap-1">
              <Button onClick={stopRecording} size="icon" className="h-8 w-8 bg-red-500 hover:bg-red-600 animate-pulse" data-testid="stop-record-btn">
                <Square className="w-3 h-3 fill-current" />
              </Button>
              <Button onClick={cancelRecording} size="icon" variant="ghost" className="h-8 w-8" data-testid="cancel-record-btn">
                <MicOff className="w-3 h-3" />
              </Button>
            </div>
          ) : (
            <Button onClick={startRecording} size="icon" variant="outline" disabled={loading || isTranscribing}
              className={cn("h-8 w-8", liveMode && "border-violet-300 text-violet-600")} data-testid="voice-btn">
              {isTranscribing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mic className="w-3.5 h-3.5" />}
            </Button>
          )}
          
          <Input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder={liveMode ? "Live mode — speak or type..." : "Ask Orka AI about this message..."}
            className="flex-1 h-8 text-xs"
            disabled={isRecording || isTranscribing}
            data-testid="ai-panel-input"
          />
          
          <Button onClick={() => sendMessage()} disabled={!input.trim() || loading} size="icon" className="h-8 w-8 bg-gradient-to-r from-violet-600 to-indigo-600" data-testid="ai-panel-send">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SwitchboardAIPanel;
